import { useCallback, useRef, useEffect } from 'react';

type PointNumericField =
  | 'nominal'
  | 'applied'
  | 'appliedAsFound'
  | 'appliedAsLeft'
  | 'asFound'
  | 'asLeft'
  | 'appliedWeight'
  | 'appliedTestWeight'
  | 'referenceLoad'
  | 'initialWeight'
  | 'weightAdded'
  | 'asFoundFinal'
  | 'asLeftFinal';

type Point = {
  id: string;
  rowIndex?: number;
  nominal?: number;
  applied?: number | null;
  appliedAsFound?: number | null;
  appliedAsLeft?: number | null;
  asFound?: number | null;
  asLeft?: number | null;
  appliedWeight?: number;
  appliedTestWeight?: number;
  referenceLoad?: number;
  initialWeight?: number | null;
  weightAdded?: number | null;
  asFoundFinal?: number | null;
  asLeftFinal?: number | null;
};

type UpdateCallback = (field: string, value: number | null) => void;

type ClickTracker = { [key: string]: number };
type FirstClickTracker = { [key: string]: boolean };

export const useTouchModeActions = (point: Point, readability: number) => {
  const DOUBLE_CLICK_THRESHOLD = 200;

  const pointRef = useRef(point);
  const readabilityRef = useRef(readability);
  const lastClickTimeRef = useRef<ClickTracker>({});
  const firstClickTrackerRef = useRef<FirstClickTracker>({});
  const lastPointIdRef = useRef<string>(point.id);

  useEffect(() => {
    if (point.id !== lastPointIdRef.current) {
      firstClickTrackerRef.current = {};
      lastClickTimeRef.current = {};
      lastPointIdRef.current = point.id;
    }
    pointRef.current = point;
    readabilityRef.current = readability;
  }, [point, readability]);

  const getCurrentValue = useCallback((field: string): number => {
    const currentPoint = pointRef.current;

    // Check if field has a value in the point
    const fieldValue = currentPoint[field as PointNumericField];
    if (typeof fieldValue === 'number') {
      return fieldValue;
    }

    // For measured fields, fall back to applied value if available
    if (field === 'asFound') {
      if (typeof currentPoint.appliedAsFound === 'number') {
        return currentPoint.appliedAsFound;
      }
      if (typeof currentPoint.applied === 'number') {
        return currentPoint.applied;
      }
      if (typeof currentPoint.appliedWeight === 'number') {
        return currentPoint.appliedWeight;
      }
    }

    if (field === 'asLeft') {
      if (typeof currentPoint.appliedAsLeft === 'number') {
        return currentPoint.appliedAsLeft;
      }
      if (typeof currentPoint.applied === 'number') {
        return currentPoint.applied;
      }
      if (typeof currentPoint.appliedWeight === 'number') {
        return currentPoint.appliedWeight;
      }
    }

    // For applied fields, fall back to nominal value if available
    if (
      field === 'applied' ||
      field === 'appliedAsFound' ||
      field === 'appliedAsLeft'
    ) {
      if (typeof currentPoint.nominal === 'number') {
        return currentPoint.nominal;
      }
      if (typeof currentPoint.appliedWeight === 'number') {
        return currentPoint.appliedWeight;
      }
    }

    // Default to 0 only as last resort
    return 0;
  }, []);

  const calculateNewValue = useCallback(
    (currentValue: number, increment: boolean, multiplier: number): number => {
      const currentReadability = readabilityRef.current;

      // Determine decimal places from readability
      const readabilityStr = currentReadability.toString();
      const decimalIndex = readabilityStr.indexOf('.');
      const decimalPlaces =
        decimalIndex === -1 ? 0 : readabilityStr.length - decimalIndex - 1;

      // Use integer math to avoid floating point errors
      const scale = Math.pow(10, decimalPlaces);
      const currentScaled = Math.round(currentValue * scale);
      const readabilityScaled = Math.round(currentReadability * scale);
      const adjustmentAmount = readabilityScaled * multiplier;

      const newValueScaled = increment
        ? currentScaled + adjustmentAmount
        : currentScaled - adjustmentAmount;

      // Convert back to decimal with proper precision
      return parseFloat((newValueScaled / scale).toFixed(decimalPlaces));
    },
    []
  );

  const handleValueUpdate = useCallback(
    (field: string, increment: boolean, onUpdate: UpdateCallback) => {
      const clickKey = `${field}-${increment ? 'inc' : 'dec'}`;
      const now = Date.now();

      const currentValue = getCurrentValue(field);

      const isFirstClick = !firstClickTrackerRef.current[field];

      if (isFirstClick) {
        firstClickTrackerRef.current[field] = true;
        lastClickTimeRef.current[clickKey] = now;
        onUpdate(field, currentValue);
      } else {
        const lastClick = lastClickTimeRef.current[clickKey] || 0;
        const isDoubleClick = now - lastClick < DOUBLE_CLICK_THRESHOLD;
        lastClickTimeRef.current[clickKey] = now;

        const multiplier = isDoubleClick ? 10 : 1;
        const newValue = calculateNewValue(currentValue, increment, multiplier);

        onUpdate(field, newValue);
      }
    },
    [getCurrentValue, calculateNewValue]
  );

  const incrementValue = useCallback(
    (field: string, onUpdate: UpdateCallback) => {
      handleValueUpdate(field, true, onUpdate);
    },
    [handleValueUpdate]
  );

  const decrementValue = useCallback(
    (field: string, onUpdate: UpdateCallback) => {
      handleValueUpdate(field, false, onUpdate);
    },
    [handleValueUpdate]
  );

  return {
    incrementValue,
    decrementValue
  };
};
